import React, { useState, useEffect } from 'react'
import { AppProvider, useApp } from './context/AppContext.jsx'
import {
  AUTH_TOKEN, CURRENT_USER, CLIENT_CONFIG, ONBOARDING_KEY, BASE_ID,
  loadClientConfig, logoutUser, navSetUrl, getAuthHeaders, TABLE_IDS,
} from './globals.js'

import StrategyOverview from './components/StrategyOverview.jsx'
import FollowupCenter from './components/FollowupCenter.jsx'
import ContactsSection from './components/ContactsSection.jsx'
import ActivityTracker from './components/ActivityTracker.jsx'
import EventsHub from './components/EventsHub.jsx'
import ConsultingPipeline from './components/ConsultingPipeline.jsx'
import QuotesHub from './components/quotes/QuotesHub.jsx'
import InsightsView from './components/InsightsView.jsx'
import CampaignsHub from './components/CampaignsHub.jsx'
import MessageLab from './components/MessageLab.jsx'
import ContentLab from './components/ContentLab.jsx'
import LandingsHub from './components/LandingsHub.jsx'
import ReportBuilder from './components/ReportBuilder.jsx'
import CPBriefings from './components/CPBriefings.jsx'
import SolutionsHub from './components/SolutionsHub.jsx'
import RequestsHub from './components/RequestsHub.jsx'
import ICPSection from './components/ICPSection.jsx'
import MethodLibrary from './components/MethodLibrary.jsx'
import CoachingHub from './components/coaching/CoachingHub.jsx'
import OwnerSpace from './components/owner/OwnerSpace.jsx'
import TeamView from './components/team/TeamView.jsx'
import TeamNotice from './components/team/TeamNotice.jsx'
import ReturnToFollowup from './components/followup/ReturnToFollowup.jsx'
import TimerBar from './components/owner/TimerBar.jsx'
import TodayHub from './components/today/TodayHub.jsx'
import { resolvePage, pageLabelOf, ownerTabOf, pageOfOwnerTab, coachingTabOf, pageOfCoachingTab, COACHING_PAGES, OWNER_PAGES, REPORT_PAGES, reportTabOf, pageOfReportTab } from './navigation.js'
import DiagnosticsList from './components/operations/DiagnosticsList.jsx'
import ReportsHub from './components/operations/ReportsHub.jsx'
import ReturnToList from './components/operations/ReturnToList.jsx'
import Directory, { DirectoryTabs } from './components/operations/Directory.jsx'
import ClientFile, { storeClientTarget } from './components/clients/ClientFile.jsx'
import ExpensesHub from './components/finance/ExpensesHub.jsx'
import { clearAllReturns } from './components/operations/ReturnToList.jsx'
import MainNav from './components/MainNav.jsx'
import LoginScreen from './components/LoginScreen.jsx'
import OnboardingWizard from './components/OnboardingWizard.jsx'
import SettingsModal from './components/SettingsModal.jsx'
import GlobalSearchModal from './components/GlobalSearchModal.jsx'
import ShortcutsModal from './components/ShortcutsModal.jsx'
import FeedbackModal from './components/FeedbackModal.jsx'
import { ErrorState } from './components/ui/States.jsx'
import { failedTablesText, failedTableKeys } from './dataLoad.js'
import { CONFIRMED_MARK } from './todayPriorities.js'

const CONFIRMED_CHANNELS = { email: 'Email', whatsapp: 'WhatsApp', linkedin: 'LinkedIn', call: 'Call', meeting: 'Meeting' }

function OikeApp() {
  const {
    data, reportingData, loading, refreshing, api,
    loadData, initApi,
    addToData, updateInData, removeFromData,
    page, setPageAndSave,
    goToAccount, goToSolution, goToProposal, goToMessageLab, createCampaignFromInsight,
    navigateToAccountId, setNavigateToAccountId,
    navigateToStakeholderId, setNavigateToStakeholderId,
    navigateToSolId, setNavigateToSolId,
    navigateToEventId, setNavigateToEventId,
    navigateToProposalId, setNavigateToProposalId,
    navigateToAccountTab, setNavigateToAccountTab,
    campaignPrefill, setCampaignPrefill,
    toasts,
  } = useApp()

  const [isAuthenticated, setIsAuthenticated] = useState(!!AUTH_TOKEN && !!CURRENT_USER)
  const [configError, setConfigError] = useState('')
  const [showSettings, setShowSettings] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [gmailReturnStatus, setGmailReturnStatus] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showGlobalSearch, setShowGlobalSearch] = useState(false)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  // Mi espacio: shown only when the server says this login is its owner (the server checks it again on every call)
  const [ownerSpace, setOwnerSpace] = useState(false)
  useEffect(() => {
    if (!isAuthenticated || CURRENT_USER?.role !== 'admin') return
    fetch('/api/owner-space', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ action: 'status' }) })
      .then(r => setOwnerSpace(r.ok)).catch(() => setOwnerSpace(false))
  }, [isAuthenticated])

  // Links between sections (contact ↔ coaching person and processes). The server
  // still decides what each one returns: this only changes the page.
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {}
      // A person's one file: from the Directorio, ⌘K, Hoy, a process or a course, and the old links to a contact or a card
      const openClientFile = (t) => {
        const target = { contactId: t.contactId || '', personId: t.personId || '', tab: t.tab || '' }
        storeClientTarget(target)
        setPageAndSave('client')
        window.dispatchEvent(new CustomEvent('oike:openClient', { detail: target }))
      }
      if (d.page === 'client') { openClientFile(d); return }
      if (d.page === 'expenses') {
        // One expense's file (from Hoy): the screen opens it; the server checks the owner on every call
        if (d.expenseId) window.__oikePendingExpense = { scope: d.scope, expenseId: d.expenseId }
        setPageAndSave('expenses')
        if (d.expenseId) window.dispatchEvent(new CustomEvent('oike:openExpense', { detail: window.__oikePendingExpense }))
        return
      }
      if (d.page === 'coaching' && d.personId && !d.processId) { openClientFile({ personId: d.personId }); return }
      if (d.page === 'contacts' && d.contactId) {
        openClientFile({ contactId: d.contactId })
      } else if (d.page === 'accounts' && d.accountId) {
        // e.g. the diagnostic prepared from a proposal (window.__oikePendingDiagnostic says which one)
        if (d.tab) setNavigateToAccountTab(d.tab)
        setNavigateToAccountId(d.accountId)
        setPageAndSave('accounts')
      } else if (d.page === 'proposals') {
        setPageAndSave('proposals')
      } else if (d.page === 'quotes') {
        // The single file of a quote (from Hoy, Ingresos, Personas, Contactos, Empresas, Pipeline): by quote or by one of its charges
        window.__oikePendingQuote = { quoteId: d.quoteId || '', chargeId: d.chargeId || '' }
        setPageAndSave('quotes')
        window.dispatchEvent(new CustomEvent('oike:openQuote', { detail: window.__oikePendingQuote }))
      } else if (d.page === 'coaching') {
        window.__oikePendingCoaching = { processId: d.processId, personId: d.personId, at: d.at || null, section: d.section || null }
        // The same screen, under the menu entry of the part it opens (a person: Clientes › Personas; a course: Servicios › Cursos)
        setPageAndSave(d.processId ? 'coaching' : d.personId ? 'people' : d.section ? pageOfCoachingTab(d.section) : 'coaching')
        window.dispatchEvent(new CustomEvent('oike:openCoaching', { detail: window.__oikePendingCoaching }))
      }
    }
    window.addEventListener('oike:navigate', handler)
    return () => window.removeEventListener('oike:navigate', handler)
  }, [setPageAndSave, setNavigateToAccountId, setNavigateToAccountTab])

  // Other screens (e.g. the mail card of Mi espacio) open Configuración › Integraciones: Gmail is managed only there
  const [settingsTab, setSettingsTab] = useState('')
  useEffect(() => {
    const handler = (e) => { setSettingsTab(e.detail?.tab || ''); setShowSettings(true) }
    window.addEventListener('oike:openSettings', handler)
    return () => window.removeEventListener('oike:openSettings', handler)
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setShowGlobalSearch(v => !v) }
      if (e.key === '?' && !e.target.matches('input,textarea')) setShowShortcuts(v => !v)
      if (e.key === 'Escape') { setShowGlobalSearch(false); setShowShortcuts(false) }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  // Update document title with pending follow-up count
  useEffect(() => {
    const count = (data?.stakeholders || []).filter(s =>
      s._enriched && !s._enriched.contactedToday && s._enriched.focusTag && s._enriched.daysSince >= 3
    ).length
    document.title = count > 0 ? `(${count}) Oike` : 'Oike'
  }, [data])

  // Auto-connect on mount if already authenticated
  useEffect(() => {
    if (!isAuthenticated) return
    ;(async () => {
      try {
        await loadClientConfig()
        const a = initApi()
        await loadData(a)

        const urlParams = new URLSearchParams(window.location.search)
        const gmailParam = urlParams.get('gmail')
        const urlId = urlParams.get('id')
        const urlPage = urlParams.get('v')
        // Back from the Google Calendar authorization of Mi espacio
        if (urlPage === 'myspace' && urlParams.get('calendar')) setPageAndSave('myspace')
        if (urlId) {
          const targetPage = urlPage || localStorage.getItem('oike_page') || 'followup'
          if (targetPage === 'accounts') setNavigateToAccountId(urlId)
          else if (targetPage === 'solutionshub') setNavigateToSolId(urlId)
          else if (targetPage === 'events') setNavigateToEventId(urlId)
          else if (targetPage === 'proposals') setNavigateToProposalId(urlId)
        }
        if (gmailParam) {
          if (gmailParam === 'connected') localStorage.setItem('oike_gmail_connected', 'true')
          setGmailReturnStatus(gmailParam)
          setShowSettings(true)
          navSetUrl(urlPage || page, urlId || null)
        }
      } catch (e) {
        console.error('Init failed:', e)
        setConfigError('Failed to connect. Please try again.')
      }
    })()
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated) {
    return <LoginScreen onLogin={() => setIsAuthenticated(true)} />
  }

  if (configError) return (
    <div className="config-screen">
      <div className="config-box">
        <div className="logo-big" style={{ background: '#1F7A70' }}>O</div>
        <h2 style={{ marginBottom: 8 }}>Oike Sales Intelligence</h2>
        <p style={{ color: 'var(--globant-danger)', fontSize: 13 }}>{configError}</p>
        <button className="action-btn btn-primary" style={{ marginTop: 16, padding: '12px 24px' }} onClick={() => window.location.reload()}>Retry</button>
      </div>
    </div>
  )

  if (loading) return <div className="loading"><div className="spinner" /></div>

  const bgSync = () => api && loadData(api, true)

  // Method Library and Acompañamiento: Oike administrator in the central base only.
  // The server checks it again; Acompañamiento also needs the email in COACHING_COACH_EMAILS.
  // In a test environment the only login is the admin of the test copy of the central base
  const testEnvironment = CURRENT_USER?.environment === 'test'
  const isOikeAdmin = CURRENT_USER?.role === 'admin' && (BASE_ID === 'appilTHSsdc8Ioyvg' || testEnvironment)

  // Two views of the commercial data (src/testRecords.js): `data` with everything for the screens where
  // records are found and worked on (Contactos, Empresas, Solicitudes, search); `reportingData`, without
  // the "PRUEBA — …" records, for metrics, reports, pipelines, follow-ups, campaigns, events and messages
  // The commercial follow-up: Mi espacio for most logins, and the Hoy tab's lower section for the owner
  const followupCenter = <FollowupCenter embedded data={reportingData} api={api} onLogActivity={bgSync} onAddRecord={addToData} onUpdateRecord={updateInData} goToAccount={goToAccount} />
  const pages = {

    followup:     <TodayHub>{followupCenter}</TodayHub>,
    contacts:     <ContactsSection data={data} api={api} onLogActivity={bgSync} onAddRecord={addToData} onUpdateRecord={updateInData} onDeleteRecord={removeFromData} goToAccount={goToAccount} />,

    events:       <EventsHub data={reportingData} api={api} onLogActivity={bgSync} onAddRecord={addToData} onUpdateRecord={updateInData} navigateToEventId={navigateToEventId} clearNavigateEvent={() => setNavigateToEventId('')} />,
    proposals:    <ConsultingPipeline data={reportingData} api={api} onLogActivity={bgSync} onUpdateRecord={updateInData} />,
    quotes:       <QuotesHub data={reportingData} api={api} onUpdateRecord={updateInData} onAddRecord={addToData} />,
    insights:     <InsightsView data={reportingData} />,
    campaigns:    <CampaignsHub data={reportingData} api={api} onLogActivity={bgSync} onAddRecord={addToData} onUpdateRecord={updateInData} onDeleteRecord={removeFromData} campaignPrefill={campaignPrefill} clearCampaignPrefill={() => setCampaignPrefill(null)} goToMessageLab={goToMessageLab} />,
    messagelab:   <MessageLab data={reportingData} api={api} onLogActivity={bgSync} onUpdateRecord={updateInData} navigateToStakeholderId={navigateToStakeholderId} clearNavigateStakeholder={() => setNavigateToStakeholderId('')} />,
    contentlab:   <ContentLab data={reportingData} api={api} onLogActivity={bgSync} onAddRecord={addToData} onDeleteRecord={removeFromData} />,
    landings:     <LandingsHub data={reportingData} api={api} onLogActivity={bgSync} onAddRecord={addToData} onUpdateRecord={updateInData} />,
    reports:      <ReportBuilder data={reportingData} api={api} onAddRecord={addToData} onCreateCampaignFromInsight={createCampaignFromInsight} />,
    accounts:     <CPBriefings data={data} api={api} onLogActivity={bgSync} onAddRecord={addToData} onUpdateRecord={updateInData} onDeleteRecord={removeFromData} navigateToAccountId={navigateToAccountId} clearNavigate={() => setNavigateToAccountId('')} navigateToAccountTab={navigateToAccountTab} clearNavigateTab={() => setNavigateToAccountTab('')} goToAccount={goToAccount} goToProposal={goToProposal} goToMessageLab={goToMessageLab} />,

    // Gestión de clientes › Directorio · Personas: contacts and (for the coach) participants' cards in one list
    directory:    <Directory data={data} />,
    // The unified file of a person (contact and, for the coach, participant's card), in tabs by permission
    client:       <ClientFile data={data} api={api} onRefresh={bgSync} onAddRecord={addToData} goToAccount={goToAccount} canFollowUp={isOikeAdmin && ownerSpace} />,
    requests:     <RequestsHub data={data} api={api} onAddRecord={addToData} onUpdateRecord={updateInData} onDeleteRecord={removeFromData} onReload={bgSync} />,
    icp:          <ICPSection data={reportingData} goToSolution={goToSolution} api={api} onLogActivity={bgSync} onAddRecord={addToData} />,
  }
  // Comercial › Actividad e informes: the existing screens together (Actividad, Resumen comercial, Precios cotizados),
  // each unchanged; the old keys overview and solutionshub open their tab. The same key keeps it mounted.
  const reports = <ReportsHub key="reports" tab={reportTabOf(page)} onTab={(t) => setPageAndSave(pageOfReportTab(t))} views={[
    ['activity', 'Actividad', <ActivityTracker data={reportingData} api={api} onLogActivity={bgSync} onUpdateRecord={updateInData} onDeleteRecord={removeFromData} />],
    ['overview', 'Resumen comercial', <StrategyOverview data={reportingData} api={api} onUpdateRecord={updateInData} onAddRecord={addToData} onLogActivity={bgSync} />],
    ['prices', 'Precios cotizados', <SolutionsHub data={reportingData} />],
  ]} />
  for (const k of Object.keys(REPORT_PAGES)) pages[k] = reports
  if (isOikeAdmin) {
    pages.library = <MethodLibrary />
    // Operaciones › Diagnósticos: every diagnostic, each opening the existing module inside its company
    pages.diagnostics = <DiagnosticsList />
    // Acompañamiento: one screen opened on the part of each menu entry (Acompañamiento, Clientes › Personas, Servicios › Cursos);
    // the same key keeps it mounted when moving between them. The server checks the coach on every call.
    const coaching = <CoachingHub key="coaching" initialTab={coachingTabOf(page)} onTabChange={(t) => { if (pageOfCoachingTab(t) !== page) setPageAndSave(pageOfCoachingTab(t)) }} />
    for (const k of Object.keys(COACHING_PAGES)) pages[k] = coaching
    // Finanzas › Cobros y saldos: the same list and files as Cotizaciones (no copies), opened on the ones with a balance
    pages.collections = <QuotesHub key="collections" data={reportingData} api={api} onUpdateRecord={updateInData} onAddRecord={addToData} initialFilter="due" area="Finanzas" />
  }
  // Hoy opens a contact chosen as a priority in Contactos (the same record; the server decides what it returns)
  const openContactFromHoy = (contactId) => window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'contacts', contactId } }))
  // After Ale confirms in Hoy that she contacted someone (never because a channel was opened), she may log it in the
  // commercial activity too: one Outreach record through the usual commercial path, marked as confirmed
  const logContactFromHoy = async (contactId, via) => {
    const s = (data?.stakeholders || []).find(x => x.id === contactId)
    if (!s || !api) throw new Error('No se encontró el contacto en los datos comerciales.')
    const channel = CONFIRMED_CHANNELS[via]
    if (!channel) throw new Error('Ese canal no se registra en la actividad comercial.')
    const name = `${s.fields?.['Name'] || ''} ${s.fields?.['Last name'] || ''}`.trim()
    const fields = {
      'Activity Name': `${channel} to ${name} — ${new Date().toLocaleDateString('en-US')}`,
      'Account': s.fields?.['Account'] || [], 'Stakeholder': [s.id], 'Channel': channel, 'Date': new Date().toISOString(), 'Status': 'Sent',
      'Notes': `${CONFIRMED_MARK} Contacto confirmado desde Mi espacio › Hoy`, 'Logged By': CURRENT_USER?.name || '',
    }
    const rec = await api.createRecord(TABLE_IDS.outreach, fields)
    addToData('outreach', fields, rec?.id || null)
  }
  // Equipo: the commercial team of the central base (only its administration; the server checks every assignment)
  if (isOikeAdmin) pages.team = <TeamView data={data} onUpdateRecord={updateInData} />
  // The follow-up panel of Hoy writes through the usual commercial path (the same api and in-memory updates)
  const commercialApi = api ? { api, addToData, updateInData } : null
  // Mi espacio: one screen opened on the part of each menu entry (Hoy, Mi tiempo, Objetivos y revisión, Mi biblioteca,
  // Consultar, Finanzas › Ingresos, Mi marca); the same key keeps it mounted. The server lets only the owner in.
  if (isOikeAdmin && ownerSpace) {
    const owner = <OwnerSpace key="owner" tab={ownerTabOf(page)} onTab={(t) => setPageAndSave(pageOfOwnerTab(t))} onGoPage={setPageAndSave} commercial={followupCenter} commercialData={reportingData} onOpenContact={openContactFromHoy} onLogContact={logContactFromHoy} onReloadCommercial={bgSync} commercialApi={commercialApi} />
    for (const k of Object.keys(OWNER_PAGES)) pages[k] = owner
    // Finanzas › Gastos: business and personal expenses, only the owner
    pages.expenses = <ExpensesHub />
    // Comercial › Seguimientos: the same queue as under Hoy (the same records and the same writes), on its own
    pages.followups = <TodayHub title="Seguimientos" area="Comercial">{followupCenter}</TodayHub>
  }

  const isBDR = CURRENT_USER?.role === 'bdr'
  // The menu by area (src/navigation.js); the server still decides what each screen returns
  const navOpts = { isBDR, isOikeAdmin, ownerSpace }
  // A saved "Mi día" (followup) opens the owner's Hoy, where it now lives
  const shownPage = resolvePage(page, navOpts)
  const currentPageLabel = pageLabelOf(shownPage, navOpts)

  return (
    <div>
      {testEnvironment && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9998, background: '#FBF0DC', color: '#6B4510', borderTop: '1px solid #E9D3A8', fontSize: 12, fontWeight: 700, textAlign: 'center', padding: '4px 8px', pointerEvents: 'none' }}>
          ENTORNO DE PRUEBA · base de Airtable de prueba · no es producción
        </div>
      )}
      <a href="#main-content" className="skip-link">Ir al contenido</a>
      {/* Mobile top bar */}
      <div className="mobile-topbar">
        <button
          aria-label="Abrir el menú"
          onClick={() => setSidebarOpen(true)}
          style={{ background: 'none', border: 'none', color: 'var(--globant-text)', fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: '4px 6px' }}
        >&#9776;</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="logo-icon" style={{ background: '#1F7A70', width: 28, height: 28, fontSize: 14 }}>O</div>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--globant-text)' }}>{currentPageLabel}</span>
        </div>
      </div>

      <div className={'sidebar-overlay' + (sidebarOpen ? ' mob-open' : '')} onClick={() => setSidebarOpen(false)} />
      <div className={'sidebar' + (sidebarOpen ? ' mob-open' : '')}>
        <div className="sidebar-logo">
          <div className="logo-icon" style={{ background: '#1F7A70' }}>O</div>
          <div>
            <span>{CLIENT_CONFIG.name || 'Oike'}</span>
            <small>Negocio · Acompañamiento</small>
          </div>
        </div>
        <MainNav opts={navOpts} page={shownPage}
          onGo={(key, { single } = {}) => { clearAllReturns(); setPageAndSave(key); if (single) setSidebarOpen(false) }} />
        <div style={{ flexShrink: 0, padding: '12px 12px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button className="action-btn btn-ghost" style={{ width: '100%', fontSize: 12, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={() => setShowGlobalSearch(true)}>
            Buscar <span style={{ opacity: 0.6, fontSize: 10 }}>⌘K</span>
          </button>
          <button className="action-btn btn-ghost" style={{ width: '100%', fontSize: 12, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={() => api && loadData(api)}>
            Actualizar datos
          </button>
          <button className="action-btn btn-ghost" style={{ width: '100%', fontSize: 12, padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={() => { setShowSettings(true); setSidebarOpen(false) }}>
            Configuración
          </button>
        </div>
        <div className="sidebar-footer" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {CURRENT_USER && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--globant-text)' }}>{CURRENT_USER.name}</div>
              <div style={{ fontSize: 10, color: 'var(--globant-muted)' }}>{CURRENT_USER.email}</div>
              <div style={{ fontSize: 9, marginTop: 2 }}>
                <span style={{ background: CURRENT_USER.role === 'admin' ? 'rgba(91,191,181,0.2)' : 'rgba(96,165,250,0.2)', color: CURRENT_USER.role === 'admin' ? 'var(--globant-green)' : 'var(--globant-info)', padding: '2px 8px', borderRadius: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {CURRENT_USER.role || 'user'}
                </span>
              </div>
            </div>
          )}
          <button
            onClick={() => setShowFeedback(true)}
            style={{ width: '100%', padding: '7px 12px', borderRadius: 8, cursor: 'pointer', background: 'rgba(30,42,44,0.04)', border: '1px solid var(--globant-border)', color: 'var(--globant-muted)', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 6 }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(30,42,44,0.08)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(30,42,44,0.04)' }}>
            Enviar comentarios
          </button>
          <button
            onClick={() => { if (confirm('¿Cerrar sesión en Oike?')) logoutUser() }}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, cursor: 'pointer', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#B4362C', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.18)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)' }}>
            Cerrar sesión
          </button>
        </div>
      </div>

      <main className="main" id="main-content" tabIndex={-1}>
        {isOikeAdmin && ownerSpace && <TimerBar />}
        {refreshing && (
          <div style={{ position: 'fixed', top: 8, right: 20, zIndex: 999, background: 'rgba(91,191,181,0.15)', border: '1px solid rgba(91,191,181,0.3)', borderRadius: 8, padding: '6px 14px', fontSize: 11, color: 'var(--globant-green)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>🔄</span> Sincronizando…
          </div>
        )}
        {failedTableKeys(data).length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <ErrorState message={`No se pudo cargar: ${failedTablesText(data.loadErrors, failedTableKeys(data))}. Lo que ves de esas partes puede estar incompleto; no se muestran como vacías.`} onRetry={bgSync} />
          </div>
        )}
        {CURRENT_USER?.teamMode && <TeamNotice />}
        {isOikeAdmin && ownerSpace && <ReturnToFollowup page={shownPage} />}
        <ReturnToList page={shownPage} />
        {(shownPage === 'directory' || shownPage === 'accounts') && <DirectoryTabs page={shownPage} />}
        {pages[shownPage] || pages.followup}
      </main>

      {showSettings && <SettingsModal onClose={() => { setShowSettings(false); setGmailReturnStatus(''); setSettingsTab('') }} gmailReturnStatus={gmailReturnStatus} initialTab={settingsTab} centralAdmin={isOikeAdmin} onOpenTeam={() => { setShowSettings(false); setSettingsTab(''); setPageAndSave('team') }} />}

      {showGlobalSearch && (
        <GlobalSearchModal
          data={data}
          onClose={() => setShowGlobalSearch(false)}
          onNavigate={(section, id, record) => {
            if (section === 'accounts' && id) {
              goToAccount(id)
            } else if (section === 'contacts' && id) {
              window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'client', contactId: id } }))
            } else {
              setPageAndSave(section)
            }
          }}
        />
      )}

      {showShortcuts && <ShortcutsModal onClose={() => setShowShortcuts(false)} />}
      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}

      {toasts.length > 0 && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 360 }}>
          {toasts.map(t => (
            <div key={t.id} style={{
              padding: '12px 16px', borderRadius: 10, fontSize: 13, fontWeight: 500,
              background: t.type === 'success' ? 'rgba(74,222,128,0.15)' : t.type === 'error' ? 'rgba(239,68,68,0.15)' : t.type === 'warning' ? 'rgba(251,191,36,0.15)' : 'rgba(96,165,250,0.15)',
              border: `1px solid ${t.type === 'success' ? 'rgba(74,222,128,0.4)' : t.type === 'error' ? 'rgba(239,68,68,0.4)' : t.type === 'warning' ? 'rgba(251,191,36,0.4)' : 'rgba(96,165,250,0.4)'}`,
              color: t.type === 'success' ? '#1E7B4F' : t.type === 'error' ? '#B4362C' : t.type === 'warning' ? '#9A6212' : '#2D5F99',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)', animation: 'slideInRight 0.25s ease',
            }}>
              {t.type === 'success' ? '✅ ' : t.type === 'error' ? '❌ ' : t.type === 'warning' ? '⚠️ ' : 'ℹ️ '}{t.msg}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(error) { return { error } }
  componentDidCatch(error, info) { console.error('Oike crash:', error, info) }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#12121F', color: '#1E2A2C', fontFamily: 'Inter, sans-serif', padding: 40 }}>
          <div style={{ maxWidth: 480, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, color: '#5F6B10' }}>Something went wrong</h2>
            <p style={{ fontSize: 13, color: '#56625F', marginBottom: 24 }}>The application encountered an unexpected error. Try reloading the page.</p>
            <pre style={{ fontSize: 11, color: '#B4362C', background: '#1E1E32', padding: '12px 16px', borderRadius: 8, textAlign: 'left', overflowX: 'auto', marginBottom: 20 }}>
              {this.state.error.message}
            </pre>
            <button onClick={() => window.location.reload()} style={{ padding: '10px 24px', background: '#BFD730', color: '#1A1A2E', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
              🔄 Recargar
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <OikeApp />
      </AppProvider>
    </ErrorBoundary>
  )
}
